import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ValidationError, object, string } from 'yup';
import { FormsUtilsStyle } from '../../../styles/components/common/forms';
import { DropDown, Input } from '../../common/ui';

export interface IAddressBookCreateFormValues {
  name: string;
  alias: string;
  address: string;
  postCode: string;
  city: string;
  countryCode?: string;
  taxId?: string;
}

export interface IAddressBookCreateFormProps {
  values: IAddressBookCreateFormValues;
  countries: { value: string; label: string }[];
  labels: {
    aliasLabel?: string;
    aliasPlaceholder: string;
    nameLabel?: string;
    namePlaceholder: string;
    addressLabel?: string;
    addressPlaceholder: string;
    postCodeLabel?: string;
    postCodePlaceholder: string;
    cityLabel?: string;
    cityPlaceholder: string;
    countryLabel?: string;
    countryPlaceholder: string;
    referenceSectionTitle: string;
    nameSectionTitle: string;
    addressSectionTitle: string;
    taxIdLabel?: string;
    taxIdPlaceholder?: string;
    formValidation: {
      requiredField: string;
    };
  };
  onFormChange: (data: any) => void;
}

const initialAddressBookCreateFormValuesState: IAddressBookCreateFormValues = {
  name: '',
  alias: '',
  address: '',
  postCode: '',
  city: '',
};

const AddressBookCreate = (props: IAddressBookCreateFormProps) => {
  const validationSchema = object().shape({
    name: string().required(props.labels.formValidation.requiredField),
    address: string().required(props.labels.formValidation.requiredField),
    city: string().required(props.labels.formValidation.requiredField),
    postCode: string().required(props.labels.formValidation.requiredField),
    countryCode: string().required(props.labels.formValidation.requiredField),
  });

  const { register, control, reset, formState, getValues } =
    useForm<IAddressBookCreateFormValues>({
      defaultValues: initialAddressBookCreateFormValuesState,
      resolver: yupResolver(validationSchema),
      mode: 'onBlur',
    });

  const [formTouchedFields, setFormTouchedFields] = useState<any>({});

  const [formErrorMessages, setFormErrorMessages] = useState<{
    alias: string | undefined;
    name: string | undefined;
    address: string | undefined;
    city: string | undefined;
    postCode: string | undefined;
    countryCode: string | undefined;
  }>({
    alias: undefined,
    name: undefined,
    address: undefined,
    city: undefined,
    postCode: undefined,
    countryCode: undefined,
  });

  const validateForm = async () => {
    try {
      await validationSchema.validate(getValues(), {
        abortEarly: false,
      });
      setFormErrorMessages({
        alias: undefined,
        name: undefined,
        address: undefined,
        city: undefined,
        postCode: undefined,
        countryCode: undefined,
      });
    } catch (error) {
      const formErrors = (error as any).inner as ValidationError[];
      const currentFormErrorMessages = { ...formErrorMessages } as any;
      Object.keys(currentFormErrorMessages).forEach((x) => {
        const err = formErrors.find((e) => e.path === x);
        if (!!err) {
          currentFormErrorMessages[x] = err.message;
        } else {
          currentFormErrorMessages[x] = undefined;
        }
      });
      setFormErrorMessages({ ...currentFormErrorMessages });
    }
  };

  useEffect(() => {
    reset(props.values);
  }, [reset, props]);

  return (
    <div>
      <form
        noValidate
        onBlur={async () => {
          await validateForm();
        }}
        onChange={async () => {
          await validateForm();
          props.onFormChange({
            values: getValues(),
            labels: props.labels,
            countries: props.countries,
            onFormChange: props.onFormChange,
          });
        }}>
        <h4>{props.labels.referenceSectionTitle}</h4>

        <Controller
          name="alias"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Input
              placeholder={props.labels.aliasPlaceholder}
              name="alias"
              label={props.labels.aliasLabel}
              type="text"
              id="alias"
              value={value}
              onChange={(e: any) => {
                onChange(e.target.value);
              }}
            />
          )}
        />

        <h4>{props.labels.nameSectionTitle}</h4>

        <Controller
          name="name"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Input
              placeholder={props.labels.namePlaceholder}
              label={props.labels.nameLabel}
              name="name"
              type="text"
              id="name"
              error={
                formTouchedFields.name ? formErrorMessages?.name : undefined
              }
              value={value}
              onBlur={() => {
                setFormTouchedFields({ ...formTouchedFields, name: true });
              }}
              onChange={(e: any) => {
                onChange(e.target.value);
              }}
            />
          )}
        />
        {(props.labels.taxIdLabel || props.labels.taxIdPlaceholder) && (
          <Controller
            name="taxId"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Input
                placeholder={props.labels.taxIdPlaceholder}
                name="taxId"
                label={props.labels.taxIdLabel}
                type="text"
                id="taxId"
                value={value}
                onChange={(e: any) => onChange(e.target.value)}
              />
            )}
          />
        )}

        <h4>{props.labels.addressSectionTitle}</h4>

        <Controller
          name="address"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Input
              placeholder={props.labels.addressPlaceholder}
              label={props.labels.addressLabel}
              name="address"
              type="text"
              id="address"
              error={
                formTouchedFields.address
                  ? formErrorMessages?.address
                  : undefined
              }
              value={value}
              onBlur={() => {
                setFormTouchedFields({ ...formTouchedFields, address: true });
              }}
              onChange={(e: any) => {
                onChange(e.target.value);
              }}
            />
          )}
        />

        <div className={FormsUtilsStyle['two-columns-fields']}>
          <Controller
            name="city"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Input
                placeholder={props.labels.cityPlaceholder}
                label={props.labels.cityLabel}
                name="city"
                type="text"
                id="city"
                error={
                  formTouchedFields.city ? formErrorMessages?.city : undefined
                }
                value={value}
                onBlur={() => {
                  setFormTouchedFields({ ...formTouchedFields, city: true });
                }}
                onChange={(e: any) => {
                  onChange(e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="postCode"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Input
                placeholder={props.labels.postCodePlaceholder}
                label={props.labels.postCodeLabel}
                name="postCode"
                type="text"
                id="postCode"
                error={
                  formTouchedFields.postCode
                    ? formErrorMessages?.postCode
                    : undefined
                }
                value={value}
                onBlur={() => {
                  setFormTouchedFields({
                    ...formTouchedFields,
                    postCode: true,
                  });
                }}
                onChange={(e: any) => {
                  onChange(e.target.value);
                }}
              />
            )}
          />
        </div>

        <Controller
          name="countryCode"
          control={control}
          defaultValue={props.values.countryCode}
          render={({ field: { onChange, value } }) => (
            <DropDown
              placheholder={props.labels.countryPlaceholder}
              name="countryCode"
              label={props.labels.countryLabel}
              id="countryCode"
              error={
                formTouchedFields.countryCode
                  ? formErrorMessages?.countryCode
                  : undefined
              }
              value={value}
              options={props.countries}
              onChange={(e: any) => {
                setFormTouchedFields({
                  ...formTouchedFields,
                  countryCode: true,
                });
                onChange(e.target.value);
              }}
            />
          )}
        />
      </form>
    </div>
  );
};

export default AddressBookCreate;
